// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { MovementFilter } from '@/movement/api/get-movements';
import { Movement } from '@/movement/api/types';
import { MovementTableRow } from '@/movement/components/movement-table-row';
import { useMovements } from '@/movement/hooks/use-movements';
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect } from 'react';

interface Props {
  filter?: MovementFilter;
  onPageChange: (pageNumber: number) => void;
  isBulkMode: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (movement: Movement) => void;
  onToggleSelectAll: (movements: Movement[]) => void;
}

export function MovementTable(props: Props) {
  const { movements, resultCount } = useMovements({
    filter: props.filter,
  });

  const pageNumber = props.filter?.pageNumber ?? 1;
  const onPageChange = props.onPageChange;

  // Handle page overrun — only redirect if we're beyond page 1 and the server returned results
  // for the query overall (resultCount > 0) to avoid infinite loops when there are genuinely no results
  useEffect(() => {
    if (movements.length === 0 && pageNumber > 1 && resultCount > 0) {
      onPageChange(1);
    }
  }, [movements.length, pageNumber, resultCount, onPageChange]);

  if (movements.length === 0) {
    return <EmptyState title="No movements found" />;
  }

  const selectableMovements = movements.filter(
    (m) => m.shipmentNumber === null
  );
  const allSelected =
    selectableMovements.length > 0 &&
    selectableMovements.every((m) => props.selectedIds.has(m.id));
  const someSelected =
    !allSelected &&
    selectableMovements.some((m) => props.selectedIds.has(m.id));

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              {props.isBulkMode && (
                <TableCell
                  padding="none"
                  sx={{ width: 32, textAlign: 'center' }}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={() => props.onToggleSelectAll(movements)}
                    size="small"
                    sx={{ p: 0 }}
                  />
                </TableCell>
              )}
              <TableCell width="14%">Serial</TableCell>
              <TableCell width="16%">Origin</TableCell>
              <TableCell width="12%">Shipped on</TableCell>
              <TableCell width="16%">Destination</TableCell>
              <TableCell width="12%">Delivered on</TableCell>
              <TableCell width="14%">Related shipment</TableCell>
              <TableCell width="14%">Last update</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {movements.map((movement) => (
              <MovementTableRow
                key={movement.id}
                movement={movement}
                isBulkMode={props.isBulkMode}
                isSelected={props.selectedIds.has(movement.id)}
                onToggleSelect={props.onToggleSelect}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page: pageNumber,
          pageSize: props.filter?.pageSize ?? 50,
          onPageChange,
        }}
        totalCount={resultCount}
        entityOptions={{ singular: 'movement', includeCount: false }}
      />
    </>
  );
}
